import { useState, useMemo, useLayoutEffect, useRef } from 'react';
import { Page } from '@/api';
import { calculateA4HeightAndAspectRatio } from '@/utils';

// Layout constants
const COL_GAP = 16;
const SIDE_PADDING = 24; // 24px gutter on each side of the viewer (multi-column layouts)
const SINGLE_COLUMN_WIDTH_RATIO = 0.7; // Single-column layout: pages take 70% of viewer width
// Width budget per page wrapper minus the wrapper's 8px-on-each-side padding (`PdfPageGrid`
// renders pages with `padding: '8px'`, so the actual PDF width is `dynamicWidth - 16`).
const PAGE_WRAPPER_PADDING_TOTAL = 16;
// Per-row vertical chrome added on top of the tallest page's height: 8px wrapper padding
// top + 8px wrapper padding bottom + label tab (~24px hanging below the PDF) + page-number
// row (~16px text + 8px mt) + extra row padding so rows aren't visually cramped. Tunable.
const ROW_CHROME_HEIGHT = 60;
// A4 portrait aspect ratio (height / width). Default when a page has no/invalid dimensions.
const A4_ASPECT_RATIO = 297 / 210;

interface UsePdfViewerLayoutProps {
  pages: Page[] | null;
  columnSize: number;
  storePageScale: number;
}

interface UsePdfViewerLayoutReturn {
  parentRef: React.RefObject<HTMLDivElement | null>;
  containerWidth: number | null;
  /** Default per-page width budget. For 1/3/4-col layouts every row uses this; for
   *  2-col it's the upper bound and `rowPageWidths` may be smaller per row. */
  dynamicWidth: number;
  /** Per-row page width (one entry per row). Same as `dynamicWidth` for non-2-col;
   *  for 2-col the row width is capped per-row by that row's tallest page so a
   *  row of pure-landscape pages can fill the column width even if the document
   *  also contains tall portrait pages elsewhere. */
  rowPageWidths: number[];
  gapSize: number;
  rowHeights: number[];
  totalRows: number;
}

export const usePdfViewerLayout = ({
  pages,
  columnSize,
  storePageScale,
}: UsePdfViewerLayoutProps): UsePdfViewerLayoutReturn => {
  const parentRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [containerHeight, setContainerHeight] = useState<number | null>(null);

  // Calculate gap size based on column count
  const gapSize = useMemo(() => {
    return COL_GAP; // Use consistent gap size for all column layouts
  }, []);

  // Layout effect for container measurement (width + height — height drives the
  // 2-column "fit a whole row in the viewport" constraint below).
  useLayoutEffect(() => {
    const update = () => {
      if (parentRef.current) {
        setContainerWidth(parentRef.current.clientWidth);
        setContainerHeight(parentRef.current.clientHeight);
      }
    };

    update();
    const resizeObserver = new ResizeObserver(update);
    if (parentRef.current) resizeObserver.observe(parentRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Pure width-based per-column budget. No height constraint here; for 2-col the
  // height-based fit is applied per-row in the rowPageWidths calc below so each row
  // can independently fill the available width based on its OWN tallest page (rather
  // than being constrained by the document's tallest portrait page).
  const dynamicWidth = useMemo(() => {
    if (!containerWidth) return 0;

    if (columnSize === 1) {
      return Math.floor(containerWidth * SINGLE_COLUMN_WIDTH_RATIO);
    }

    const gapSpace = (columnSize - 1) * COL_GAP;
    const availableWidth = containerWidth - SIDE_PADDING * 2 - gapSpace;
    return Math.floor(availableWidth / columnSize);
  }, [containerWidth, columnSize]);

  // Per-row page widths AND per-row heights, computed together from each page's
  // true aspect ratio (Page.pageDimensions).
  // - For 1/3/4-col: every row uses dynamicWidth (uniform across the doc).
  // - For 2-col: each row's width is capped independently so that row's tallest page
  //   still fits within the viewer height. A row of two landscape pages will fill
  //   the full half-width budget; a row containing a tall portrait page will shrink.
  // Falls back to A4 if a page's pageDimensions are missing/zero (handled inside
  // calculateA4HeightAndAspectRatio).
  const { rowPageWidths, rowHeights } = useMemo(() => {
    if (!pages || !containerWidth || dynamicWidth <= 0) {
      return { rowPageWidths: [] as number[], rowHeights: [] as number[] };
    }
    const TWO_COL_HEIGHT_TARGET = 0.95;
    const availableHeight =
      containerHeight !== null ? Math.max(0, containerHeight * TWO_COL_HEIGHT_TARGET - ROW_CHROME_HEIGHT) : 0;

    const totalRows = Math.ceil(pages.length / columnSize);
    const widths: number[] = [];
    const heights: number[] = [];

    for (let rowIndex = 0; rowIndex < totalRows; rowIndex++) {
      const start = rowIndex * columnSize;
      const end = Math.min(start + columnSize, pages.length);

      // Find this row's tallest aspect (height / width).
      let rowMaxAspect = 0;
      for (let i = start; i < end; i++) {
        const p = pages[i];
        const pw = p.pageDimensions?.width ?? 0;
        const ph = p.pageDimensions?.height ?? 0;
        if (pw > 0 && ph > 0) {
          const a = ph / pw;
          if (a > rowMaxAspect) rowMaxAspect = a;
        }
      }
      if (rowMaxAspect === 0) rowMaxAspect = A4_ASPECT_RATIO;

      // 2-col: cap this row's page width by the height-based fit using THIS row's
      // tallest aspect. Other column counts use the full width budget unchanged.
      let rowPageBudget = dynamicWidth;
      if (columnSize === 2 && containerHeight) {
        const heightBasedRenderWidth = Math.floor(availableHeight / (rowMaxAspect * storePageScale));
        const heightBasedSize = heightBasedRenderWidth + PAGE_WRAPPER_PADDING_TOTAL;
        rowPageBudget = Math.max(0, Math.min(dynamicWidth, heightBasedSize));
      }
      widths[rowIndex] = rowPageBudget;

      // Row height = tallest page height in this row at this row's pageBudget + chrome.
      const pageRenderWidth = Math.max(0, rowPageBudget - PAGE_WRAPPER_PADDING_TOTAL);
      let maxPageHeight = 0;
      for (let i = start; i < end; i++) {
        const page = pages[i];
        const pageHeight =
          calculateA4HeightAndAspectRatio({
            width: page.pageDimensions?.width,
            height: page.pageDimensions?.height,
            newWidth: pageRenderWidth,
          }) * storePageScale;
        if (pageHeight > maxPageHeight) maxPageHeight = pageHeight;
      }
      heights[rowIndex] = maxPageHeight + ROW_CHROME_HEIGHT;
    }
    return { rowPageWidths: widths, rowHeights: heights };
  }, [pages, columnSize, dynamicWidth, storePageScale, containerWidth, containerHeight]);

  const totalPages = pages?.length || 0;
  const totalRows = Math.ceil(totalPages / columnSize);

  return {
    parentRef,
    containerWidth,
    dynamicWidth,
    rowPageWidths,
    gapSize,
    rowHeights,
    totalRows,
  };
};
